import classnames from 'classnames';
import { uniqBy, throttle } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Container, Grid, Cell } from 'components/Grid';
import PageSectionSecondaryNavigationItem from 'components/PageSectionSecondaryNavigationItem';
import ScrollTo from 'components/ScrollTo';
import { small, medium, mediumUp, largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import Device from 'utils/Device';

class PageSectionSecondaryNavigation extends React.PureComponent {
  static get viewportVerticalOffset() {
    return -(
      Theme.get('secondary-navigation-height') +
      Theme.get('top-navigation-height') +
      Theme.get('spacing') * 4
    );
  }

  static refIsInViewport(ref) {
    if (!ref.current) {
      return false;
    }

    const rect = ref.current.getBoundingClientRect();
    const offset = {
      left: 0,
      right: 0,
      top: PageSectionSecondaryNavigation.viewportVerticalOffset,
      bottom: PageSectionSecondaryNavigation.viewportVerticalOffset,
    };

    return (
      rect.top <= window.innerHeight + offset.bottom &&
      rect.right >= -offset.left &&
      rect.bottom >= -offset.top &&
      rect.left <= window.innerWidth + offset.right
    );
  }

  static buildIdFromIndex(index) {
    return `page-section-navigation-item-${index}`;
  }

  constructor(props) {
    super(props);

    this.navigationItems = uniqBy(this.props.items, 'navigationTabText');
    this.itemRefs = this.props.items.map(() => React.createRef());
    this.navigationItemRefs = this.navigationItems.map(() => React.createRef());
    this.navigationRef = React.createRef();
    this.state = {
      appearance: 'Dark',
      indicatorWidth: 0,
      indicatorOffsetLeft: 0,
    };
  }

  componentDidMount() {
    window.addEventListener('scroll', this.throttledSetIndicatorState());
    window.addEventListener('resize', this.throttledSetIndicatorState());

    this.setIndicatorState();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.throttledSetIndicatorState());
    window.removeEventListener('resize', this.throttledSetIndicatorState());
  }

  get panelClasses() {
    return classnames('PageSectionSecondaryNavigation__panel', {
      'PageSectionSecondaryNavigation__panel--light': this.state.appearance === 'Light',
      'PageSectionSecondaryNavigation__panel--neutral': this.state.appearance === 'Neutral',
      'PageSectionSecondaryNavigation__panel--dark': this.state.appearance === 'Dark',
    });
  }

  get itemInViewportIndex() {
    return this.itemRefs.findIndex(ref => PageSectionSecondaryNavigation.refIsInViewport(ref));
  }

  get itemInViewport() {
    return this.props.items[this.itemInViewportIndex];
  }

  get activeNavigationTabIndex() {
    return this.navigationItems.findIndex(
      navigationItem => navigationItem.navigationTabText === this.itemInViewport.navigationTabText
    );
  }

  get activeNavigationTabRef() {
    return this.navigationItemRefs[this.activeNavigationTabIndex];
  }

  get shouldSetState() {
    return this.itemInViewportIndex > -1;
  }

  get shouldHideHeading() {
    return this.itemInViewportIndex > 0 && Device.isSmall;
  }

  setNavigationScrollLeft() {
    this.navigationRef.current.scrollLeft =
      this.activeNavigationTabIndex > 0 ? this.activeNavigationTabRef.current.offsetLeft : 0;
  }

  setIndicatorState() {
    if (!this.shouldSetState) {
      return false;
    }

    this.setState({
      appearance: this.itemInViewport.appearance,
      indicatorWidth: this.activeNavigationTabRef.current.offsetWidth,
      indicatorOffsetLeft: this.activeNavigationTabRef.current.offsetLeft,
    });

    this.setNavigationScrollLeft();

    return true;
  }

  throttledSetIndicatorState() {
    return throttle(() => this.setIndicatorState(), 60);
  }

  itemScrollToId(navigationItem) {
    const index = this.props.items.findIndex(
      item => item.navigationTabText === navigationItem.navigationTabText
    );

    return PageSectionSecondaryNavigation.buildIdFromIndex(index);
  }

  render() {
    return (
      <div className="PageSectionSecondaryNavigation">
        <div className={this.panelClasses}>
          <Container className="PageSectionSecondaryNavigation__container">
            <Grid>
              <Cell smallUp={{ columns: 12 }}>
                <div className="PageSectionSecondaryNavigation__sub-panels">
                  {!this.shouldHideHeading && (
                    <div className="PageSectionSecondaryNavigation__sub-panel PageSectionSecondaryNavigation__sub-panel--heading">
                      <p className="PageSectionSecondaryNavigation__heading">
                        {this.props.heading}
                      </p>
                    </div>
                  )}

                  <div
                    ref={this.navigationRef}
                    className="PageSectionSecondaryNavigation__sub-panel PageSectionSecondaryNavigation__sub-panel--links"
                  >
                    <ul className="PageSectionSecondaryNavigation__list">
                      {this.navigationItems.map((navigationItem, index) => (
                        <li
                          key={navigationItem.navigationTabText}
                          className="PageSectionSecondaryNavigation__list-item"
                        >
                          <ScrollTo
                            target={this.itemScrollToId(navigationItem)}
                            className="PageSectionSecondaryNavigation__link"
                            offset={-Theme.get('secondary-navigation-height')}
                            ref={this.navigationItemRefs[index]}
                          >
                            {navigationItem.navigationTabText}
                          </ScrollTo>
                        </li>
                      ))}
                    </ul>
                    <i
                      className="PageSectionSecondaryNavigation__indicator"
                      style={{
                        width: this.state.indicatorWidth,
                        transform: `translateX(${this.state.indicatorOffsetLeft}px)`,
                      }}
                    />
                  </div>
                </div>
              </Cell>
            </Grid>
          </Container>
        </div>

        <div className="PageSectionSecondaryNavigation__items">
          {this.props.items.map((item, index) => (
            <PageSectionSecondaryNavigationItem
              id={PageSectionSecondaryNavigation.buildIdFromIndex(index)}
              item={item}
              ref={this.itemRefs[index]}
              key={item.internalName}
            >
              {this.props.renderPageSection(item.content)}
            </PageSectionSecondaryNavigationItem>
          ))}
        </div>

        <style jsx>
          {`
            .PageSectionSecondaryNavigation {
              position: relative;
            }

            .PageSectionSecondaryNavigation__panel {
              position: sticky;
              z-index: 4;
              top: ${Theme.get('top-navigation-height')}px;
              left: 0;
              transition: all ${Theme.get('transition-timing-ease-in-out')}
                ${Theme.get('transition-duration-ms')};
            }

            .PageSectionSecondaryNavigation__panel--dark {
              background-color: ${Theme.get('colors-black')};
              box-shadow: inset 0 -2px 0 0 ${Theme.get('colors-black-80')};
            }

            .PageSectionSecondaryNavigation__panel--neutral {
              background-color: ${Theme.get('colors-grey')};
              box-shadow: inset 0 -2px 0 0 ${Theme.get('colors-black-20')};
            }

            .PageSectionSecondaryNavigation__panel--light {
              background-color: ${Theme.get('colors-white')};
              box-shadow: inset 0 -2px 0 0 ${Theme.get('colors-black-20')};
            }

            .PageSectionSecondaryNavigation__sub-panels {
              display: flex;
              width: 100%;
            }

            .PageSectionSecondaryNavigation__sub-panel--links {
              position: relative;
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
              scroll-behavior: smooth;
              -ms-overflow-style: none;
              scrollbar-width: none;
            }

            .PageSectionSecondaryNavigation__sub-panel--links::-webkit-scrollbar {
              display: none;
            }

            .PageSectionSecondaryNavigation__sub-panel--heading {
              flex: 1;
            }

            .PageSectionSecondaryNavigation__panel--light .PageSectionSecondaryNavigation__heading {
              color: ${Theme.get('colors-black')};
            }

            .PageSectionSecondaryNavigation__panel--light
              .PageSectionSecondaryNavigation__indicator,
            .PageSectionSecondaryNavigation__panel--neutral
              .PageSectionSecondaryNavigation__indicator {
              background-color: ${Theme.get('colors-black')};
            }

            .PageSectionSecondaryNavigation__panel--dark .PageSectionSecondaryNavigation__heading {
              color: ${Theme.get('colors-white')};
            }

            .PageSectionSecondaryNavigation__panel--dark
              .PageSectionSecondaryNavigation__indicator {
              background-color: ${Theme.get('colors-white')};
            }

            .PageSectionSecondaryNavigation__sub-panel {
              display: flex;
              align-items: center;
              max-width: 100%;
            }

            .PageSectionSecondaryNavigation__heading {
              font-size: ${Theme.get('font-size-description-px')};
              font-weight: ${Theme.get('font-weight-bold')};
              letter-spacing: ${Theme.get('letter-spacing')};
              white-space: nowrap;
              padding-right: ${Theme.get('spacing') * 5}px;
            }

            @media ${mediumUp} {
              .PageSectionSecondaryNavigation__heading {
                font-size: ${Theme.get('font-size-px')};
              }
            }

            @media ${medium} {
              .PageSectionSecondaryNavigation__heading {
                padding-right: ${Theme.get('spacing') * 10}px;
              }

              .PageSectionSecondaryNavigation__sub-panels {
                padding: 0 ${Theme.get('spacing') * 2}px;
              }
            }

            @media ${largeUp} {
              .PageSectionSecondaryNavigation__heading {
                padding-right: ${Theme.get('spacing') * 20}px;
              }

              .PageSectionSecondaryNavigation__sub-panels {
                padding: 0 ${Theme.get('spacing') * 8}px;
              }
            }

            .PageSectionSecondaryNavigation__list {
              display: flex;
            }

            .PageSectionSecondaryNavigation__indicator {
              position: absolute;
              height: 2px;
              bottom: 0;
              z-index: 2;
              transition:
                transform ${Theme.get('transition-timing-ease-in-out')}
                  ${Theme.get('transition-duration-ms')},
                width ${Theme.get('transition-timing-ease-in-out')}
                  ${Theme.get('transition-duration-ms')};
            }
          `}
        </style>

        <style jsx global>
          {`
            @media ${small} {
              .PageSectionSecondaryNavigation__container.Container {
                padding-right: 0;
              }
            }

            .PageSectionSecondaryNavigation__link {
              text-decoration: none;
              text-transform: uppercase;
              font-size: 10px;
              letter-spacing: 2px;
              padding: ${Theme.get('spacing') * 3}px;
              font-weight: bold;
              white-space: nowrap;
            }

            @media ${mediumUp} {
              .PageSectionSecondaryNavigation__link {
                font-size: ${Theme.get('font-size-small-px')};
              }
            }

            .PageSectionSecondaryNavigation__panel--light .PageSectionSecondaryNavigation__link {
              color: ${Theme.get('colors-black')};
            }

            .PageSectionSecondaryNavigation__panel--light
              .PageSectionSecondaryNavigation__indicator {
              background-color: ${Theme.get('colors-black')};
            }

            .PageSectionSecondaryNavigation__panel--dark .PageSectionSecondaryNavigation__link {
              color: ${Theme.get('colors-white')};
            }
          `}
        </style>
      </div>
    );
  }
}

PageSectionSecondaryNavigation.propTypes = {
  heading: PropTypes.string.isRequired,
  items: PropTypes.arrayOf({
    appearance: PropTypes.string.isRequired,
    internalName: PropTypes.string.isRequired,
    navigationTabText: PropTypes.string,
    content: PropTypes.shape({}),
  }).isRequired,
  renderPageSection: PropTypes.func.isRequired,
};

export default PageSectionSecondaryNavigation;
